import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MobiLinkAnimalDto, ParamCalcDto, ParamIntDto, SentBarcodeDto, UserHerdDto } from '../../generated/tsClient';
import i18n from '../../i18n';

export interface IAppState {
  language: string,
  loading: boolean,
  error: string | null,
    showSettingsModal: boolean,
    userHerds: UserHerdDto[] | null,
    showHerdSelector: boolean,
    selectedHerd: UserHerdDto | null,
    animalList: MobiLinkAnimalDto[] | null,
    showAnimalList: boolean;
    showHelpModal: boolean;
    userCanSendToMMfi: boolean | null;
    cowListOrderOptions: ParamIntDto[] | null;
    selectedCowListOrder: number;
    barcodesSendToOptions: ParamIntDto[] | null;
    selectedBarcodesSendTo: number;
    herdCanOrderPregCheck: boolean;
    showPregCheckMessage: boolean;
    sentBarcodes: SentBarcodeDto[] | null;
    showSentBarcodesModal: boolean;
    selectedBarcodeSending: SentBarcodeDto | null;
    userIsAuthorized: boolean | null;
    showBarcodeScannerModal: boolean;
    selectedAnimal: string | null;
    showSendingPreviewModal: boolean;
    insemDaysHighlightLimits: ParamCalcDto | null;
    showConfirmationModal: boolean;
    animalListLoaded: Date | null;
    animalListLoadedMessageDate: Date | null;
}

const initialState: IAppState = {
  language: 'fi',
  loading: false,
  error: null,
    showSettingsModal: false,
    userHerds: null,
    showHerdSelector: true,
    selectedHerd: null,
    animalList: null,
    showAnimalList: false,
    showHelpModal: false,
    userCanSendToMMfi: null,
    cowListOrderOptions: null,
    selectedCowListOrder: 1,
    barcodesSendToOptions: null,
    selectedBarcodesSendTo: 1,
    herdCanOrderPregCheck: false,
    showPregCheckMessage: false, 
    sentBarcodes: null,
    showSentBarcodesModal: false,
    selectedBarcodeSending: null,
    userIsAuthorized: null,
    showBarcodeScannerModal: false,
    selectedAnimal: null,
    showSendingPreviewModal: false,
    insemDaysHighlightLimits: null,
    showConfirmationModal: false,
    animalListLoaded: null,
    animalListLoadedMessageDate: null
}

export const appSlice = createSlice({
  name: 'app',
  initialState: initialState,
  reducers: {
    setLanguage: (state, action: PayloadAction<string>) => {
      i18n.changeLanguage(action.payload);
      state.language = action.payload;
    },
    startLoading: (state) => {
      state.loading = true;
    },
    stopLoading: (state) => {
      state.loading = false;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      console.error(action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
    setShowSettingsModal: (state, action: PayloadAction<boolean>) => {
      state.showSettingsModal = action.payload;
      },
      setUserHerds: (state, action: PayloadAction<UserHerdDto[]>) => {
          state.userHerds = action.payload;
      },
      setShowHerdSelector: (state, action: PayloadAction<boolean>) => {
          state.showHerdSelector = action.payload;
      },
      setSelectedHerd: (state, action: PayloadAction<UserHerdDto | null>) => {
          state.selectedHerd = action.payload;
      },
      setAnimalList: (state, action: PayloadAction<MobiLinkAnimalDto[] | null>) => {
          state.animalList = action.payload;
      },
      setShowAnimalList: (state, action: PayloadAction<boolean>) => {
          state.showAnimalList = action.payload;
      },
      setShowHelpModal: (state, action: PayloadAction<boolean>) => {
          state.showHelpModal = action.payload;
      },
      setUserCanSendToMMfi: (state, action: PayloadAction<boolean | null>) => {
          state.userCanSendToMMfi = action.payload;
      },
      setCowListOrderOptions: (state, action: PayloadAction<ParamIntDto[]>) => {
          state.cowListOrderOptions = action.payload;
      },
      setSelectedCowListOrder: (state, action: PayloadAction<number>) => {
          state.selectedCowListOrder = action.payload;
      },
      setBarcodesSendToOptions: (state, action: PayloadAction<ParamIntDto[]>) => {
          state.barcodesSendToOptions = action.payload;
      },
      setSelectedBarcodesSendTo: (state, action: PayloadAction<number>) => {
          state.selectedBarcodesSendTo = action.payload;
      },
      setHerdCanOrderPregCheck: (state, action: PayloadAction<boolean>) => {
          state.herdCanOrderPregCheck = action.payload;
      },
      setShowPregCheckMessage: (state, action: PayloadAction<boolean>) => {
          state.showPregCheckMessage = action.payload;
      },
      setSentBarcodes: (state, action: PayloadAction<SentBarcodeDto[]>) => {
          state.sentBarcodes = action.payload;
      },
      setShowSentBarcodesModal: (state, action: PayloadAction<boolean>) => {
          state.showSentBarcodesModal = action.payload;
      },
      setSelectedBarcodeSending: (state, action: PayloadAction<SentBarcodeDto | null>) => {
          state.selectedBarcodeSending = action.payload;
      },
      setUserIsAuthorized: (state, action: PayloadAction<boolean | null>) => {
          state.userIsAuthorized = action.payload;
      },
      setShowBarcodeScannerModal: (state, action: PayloadAction<boolean>) => {
          state.showBarcodeScannerModal = action.payload;
      },
      setSelectedAnimal: (state, action: PayloadAction<string | null>) => {
          state.selectedAnimal = action.payload;
      },
      setShowSendingPreviewModal: (state, action: PayloadAction<boolean>) => {
          state.showSendingPreviewModal = action.payload;
      },
      setInsemDaysHighlightLimits: (state, action: PayloadAction<ParamCalcDto>) => {
          state.insemDaysHighlightLimits = action.payload;
      },
      setShowConfirmationModal: (state, action: PayloadAction<boolean>) => {
          state.showConfirmationModal = action.payload;
      },
      setAnimalListLoaded: (state, action: PayloadAction<Date | null>) => {
          state.animalListLoaded = action.payload;
      },
      setAnimalListLoadedMessageDate: (state, action: PayloadAction<Date | null>) => {
          state.animalListLoadedMessageDate = action.payload;
      }
  }
})

export const { setLanguage, startLoading, stopLoading, setError, clearError, setShowSettingsModal, setUserHerds,
    setShowHerdSelector, setSelectedHerd, setAnimalList, setShowAnimalList, setShowHelpModal, setUserCanSendToMMfi,
    setCowListOrderOptions, setSelectedCowListOrder, setBarcodesSendToOptions, setSelectedBarcodesSendTo,
    setHerdCanOrderPregCheck, setShowPregCheckMessage, setAnimalListLoadedMessageDate, setSentBarcodes, setShowSentBarcodesModal,
    setSelectedBarcodeSending, setUserIsAuthorized, setShowBarcodeScannerModal, setSelectedAnimal,
    setShowSendingPreviewModal, setInsemDaysHighlightLimits, setShowConfirmationModal, setAnimalListLoaded } = appSlice.actions;

export default appSlice.reducer;
